/**
 * Locations hub island loader: hydrates the server-rendered tree in place (indexable-page
 * hydration rework, following the SRP/job/salary/best-companies-dimension fix).
 * layouts/LayoutDefault.tsx's locations branch now server-renders the same +Page.tsx tree wrapped
 * in the SAME full provider stack this file builds (Provider/store, AuthProvider,
 * FeedbackModalProvider, ToastProvider, MinimalChrome) directly into #locations-ssr — there's no
 * separate #locations-app to mount a new tree into and swap in anymore, so this file just calls
 * hydrateRoot() on #locations-ssr.
 */
import React from 'react';
import { hydrateRoot } from 'react-dom/client';
import type { Root } from 'react-dom/client';
import { Provider } from 'react-redux';
import { IntlProvider } from 'react-intl';
import type { PageContextClient } from 'vike/types';

import { getReactRootSentryOptions } from '../../utils/react/react-root-sentry-options';
import { loadLocaleData } from '../../utils/routing/url-helpers';
import { buildFooterStaticStrings } from '../../utils/footer/buildFooterStaticStrings';
import { createStore } from '../../redux/createStore';
import { AuthProvider } from '../../contexts/AuthContext';
import { ExperimentProvider } from '../../contexts/ExperimentContext';
import { UserProvider } from '../../query/UserProvider';
import { FeedbackModalProvider } from '../../contexts/FeedbackModalContext';
import { ToastProvider } from '../../contexts/ToastContext';
import { FooterStringsProvider } from '../../contexts/FooterStringsContext';
import { ToastContainer } from '../../design-system/Toast';
import { MinimalChrome } from '../../layouts/MinimalChrome';
import {
  scheduleIdleCallbackWithFallback,
  cancelIdleScheduleHandle,
  type IdleScheduleHandle,
} from '../../utils/browser/scheduleIdleCallbackWithFallback';

/** Worst-case cap (ms) for the deferred hydrate path — see srp.client.ts's constant of the same name. */
const DEFER_MOUNT_FALLBACK_MS = 2500;

let locationsRoot: Root | null = null;
let locationsRootContainer: HTMLElement | null = null;

const SUPPORTED_LOCALES = ['en', 'fi', 'sv'] as const;

function getLocaleFromPathname(pathname: string): string {
  const first = pathname.split('/').filter(Boolean)[0]?.toLowerCase();
  return first && (SUPPORTED_LOCALES as readonly string[]).includes(first) ? first : 'en';
}

/**
 * Unmount the locations island root synchronously. Must be called by `+onRenderClient.ts` before
 * it creates/renders a *different* React root over `#root` (an ancestor of `#locations-ssr`) —
 * same ordering reason as `unmountSrpIsland`/`unmountJobIsland`/`unmountBestCompaniesDimensionIsland`.
 */
export function unmountLocationsIsland(): void {
  if (locationsRoot) {
    locationsRoot.unmount();
    locationsRoot = null;
    locationsRootContainer = null;
  }
}

export interface RunLocationsIslandLoaderOptions {
  /** When true (e.g. we skipped root hydration), hydrate immediately instead of deferring. */
  mountImmediately?: boolean;
}

async function hydrateLocationsIsland(container: HTMLElement, pageContext: PageContextClient): Promise<void> {
  const pathname =
    typeof window !== 'undefined' ? window.location.pathname : ((pageContext as any)?.urlPathname ?? '/');
  const locale = (pageContext?.locale as string) || getLocaleFromPathname(pathname) || 'en';
  const messages = loadLocaleData(locale);
  const footerStrings = buildFooterStaticStrings(messages);
  const store = createStore(pageContext);
  const initialUser = (pageContext?.user ?? null) as any;
  const initialHasBackofficeAccess = Boolean((pageContext as { hasBackofficeAccess?: boolean })?.hasBackofficeAccess);
  const initialExperiments = (pageContext as { experiments?: Record<string, string> })?.experiments ?? {};

  const { default: LocationsHubPageContent } = await import('./+Page');

  const tree = React.createElement(
    Provider,
    { store },
    React.createElement(
      IntlProvider,
      { messages, locale, defaultLocale: 'en' },
      React.createElement(
        FooterStringsProvider,
        { strings: footerStrings },
        React.createElement(
          AuthProvider,
          { initialUser, initialHasBackofficeAccess },
          React.createElement(
            ExperimentProvider,
            { initialVariants: initialExperiments },
          React.createElement(
            UserProvider,
            { initialUser, initialHasBackofficeAccess },
            React.createElement(
              FeedbackModalProvider,
              null,
              React.createElement(
                ToastProvider,
                null,
                React.createElement(
                  React.Fragment,
                  null,
                  React.createElement(ToastContainer),
                  React.createElement(MinimalChrome, { noPadding: true }, React.createElement(LocationsHubPageContent)),
                ),
              ),
            ),
          ),
          ),
        ),
      ),
    ),
  );

  if (locationsRoot && locationsRootContainer === container) {
    locationsRoot.render(tree);
    return;
  }

  if (locationsRoot && locationsRootContainer) {
    locationsRoot.unmount();
    locationsRoot = null;
    locationsRootContainer = null;
  }

  locationsRoot = hydrateRoot(container, tree, getReactRootSentryOptions());
  locationsRootContainer = container;
}

export function runLocationsIslandLoader(
  pageContext: PageContextClient,
  options: RunLocationsIslandLoaderOptions = {},
): void {
  const appEl = document.getElementById('locations-ssr');
  if (!appEl) return;

  let mounted = false;
  let fallbackHandle: IdleScheduleHandle | null = null;

  const doMount = (): void => {
    if (mounted) return;
    mounted = true;
    if (fallbackHandle !== null) {
      cancelIdleScheduleHandle(fallbackHandle);
      fallbackHandle = null;
    }
    void hydrateLocationsIsland(appEl, pageContext);
  };

  const onInteraction = (): void => {
    doMount();
    document.removeEventListener('pointerdown', onInteraction);
    document.removeEventListener('keydown', onInteraction);
    document.removeEventListener('focus', onInteraction, true);
  };

  document.addEventListener('pointerdown', onInteraction, { once: true, passive: true });
  document.addEventListener('keydown', onInteraction, { once: true, passive: true });
  document.addEventListener('focus', onInteraction, { once: true, capture: true });

  if (options.mountImmediately) {
    doMount();
  } else {
    fallbackHandle = scheduleIdleCallbackWithFallback(doMount, { timeout: DEFER_MOUNT_FALLBACK_MS });
  }
}
